import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatMoney } from "./offer-customer-lines";
import { includedLine, lineAmount, numberValue, offerChecks } from "./utils/offer-workspace";
import { resolveOfferLayout } from "./utils/offer-layout";
import "./offer-workspace.css";

export default function OfferReviewWorkspace({ offer, onNavigate, onPreview, onSend }) {
  const { t } = useTranslation();
  const [salesChange, setSalesChange] = useState(0);
  const [costChange, setCostChange] = useState(0);
  const [layout, setLayout] = useState(() => resolveOfferLayout(offer));
  useEffect(() => {
    setLayout(resolveOfferLayout(offer));
  }, [offer?.id, offer?.layout]);
  const checks = offerChecks(offer);
  const lines = (offer?.items || []).filter(includedLine);
  const sales = lines.reduce((sum, row) => sum + lineAmount(row), 0);
  const costKnown = offer?.cost_total != null;
  const cost = numberValue(offer?.cost_total);
  const scenarioSales = sales * (1 + numberValue(salesChange) / 100);
  const scenarioCost = cost * (1 + numberValue(costChange) / 100);
  const money = (value) => formatMoney(value, offer?.currency || "NOK");
  const blocking = checks.some((check) => check.blocking);

  return (
    <div className="offer-review-grid">
      <section className="offer-review-card">
        <h2>{t("offers.workspace.review")}</h2>
        <p>{t("offers.workspace.review_hint")}</p>
        {offer?.latest_delivery ? (
          <p className="offer-review-status" role="status">
            {t(`offers.workspace.delivery_${offer.latest_delivery.status}`)} · {offer.latest_delivery.recipient}
          </p>
        ) : null}
        <p className="offer-review-meta">
          {t("offers.workspace.outgoing_count", { count: offer?.outgoing_document_ids?.length || 0 })}
        </p>
        {checks.length ? (
          checks.map((check) => (
            <div className={`offer-check ${check.blocking ? "is-blocking" : ""}`} key={check.code}>
              <span className="offer-check-label">
                <span className={`offer-check-dot ${check.blocking ? "is-blocking" : ""}`} aria-hidden="true" />
                {t(`offers.workspace.check_${check.code}`)}
              </span>
              <button type="button" onClick={() => onNavigate(check.tab)}>
                {t("offers.workspace.fix")}
              </button>
            </div>
          ))
        ) : (
          <div className="offer-check is-ready">
            <span className="offer-check-label">
              <span className="offer-check-dot is-ready" aria-hidden="true" />
              {t("offers.workspace.ready")}
            </span>
          </div>
        )}
        <div className="offer-workspace-tools offer-review-actions">
          <label>
            {t("offers.workspace.layout")}{" "}
            <select value={layout} onChange={(e) => setLayout(e.target.value)}>
              {["simple", "document"].map((value) => (
                <option key={value} value={value}>
                  {t(`offers.workspace.layout_${value}`)}
                </option>
              ))}
            </select>
          </label>
          <button type="button" onClick={() => onPreview(layout)}>
            {t("offers.workspace.preview")}
          </button>
          <button
            type="button"
            className="offer-workspace-primary"
            disabled={blocking}
            onClick={() => onSend(layout)}
          >
            {t("offers.workspace.send")}
          </button>
        </div>
      </section>

      <section className="offer-review-card">
        <h2>{t("offers.workspace.explain")}</h2>
        <div className="offer-review-lines">
          {lines.map((row, i) => (
            <div className="offer-check" key={row.client_key || row.id || i}>
              <span>{row.title || row.description}</span>
              <strong>{money(lineAmount(row))}</strong>
            </div>
          ))}
        </div>
        <div className="offer-check offer-check-total">
          <span>{t("offers.workspace.customer_total")}</span>
          <strong>{money(sales)}</strong>
        </div>
        {costKnown ? (
          <>
            <div className="offer-check">
              <span>{t("offers.workspace.cost")}</span>
              <strong>{money(cost)}</strong>
            </div>
            <div className="offer-check">
              <span>{t("offers.workspace.margin")}</span>
              <strong>
                {money(sales - cost)} · {sales > 0 ? (((sales - cost) / sales) * 100).toFixed(1) : "—"}%
              </strong>
            </div>
          </>
        ) : (
          <p>{t("offers.workspace.cost_unknown")}</p>
        )}
      </section>

      <section className="offer-review-card">
        <h2>{t("offers.workspace.scenario")}</h2>
        <p>{t("offers.workspace.scenario_hint")}</p>
        <div className="offer-scenario-fields">
          <label>
            {t("offers.workspace.sales_change")}
            <input
              type="number"
              min="-100"
              step="1"
              value={salesChange}
              onChange={(e) => setSalesChange(e.target.value)}
            />
          </label>
          <label>
            {t("offers.workspace.cost_change")}
            <input
              type="number"
              min="-100"
              disabled={!costKnown}
              step="1"
              value={costChange}
              onChange={(e) => setCostChange(e.target.value)}
            />
          </label>
        </div>
        <div className="offer-check">
          <span>{t("offers.workspace.total")}</span>
          <strong>{money(scenarioSales)}</strong>
        </div>
        {costKnown ? (
          <div className="offer-check">
            <span>{t("offers.workspace.margin")}</span>
            <strong>
              {money(scenarioSales - scenarioCost)} ·{" "}
              {scenarioSales > 0
                ? (((scenarioSales - scenarioCost) / scenarioSales) * 100).toFixed(1)
                : "—"}
              %
            </strong>
          </div>
        ) : null}
        <div className="offer-workspace-tools">
          <button
            type="button"
            onClick={() => {
              setSalesChange(0);
              setCostChange(0);
            }}
          >
            {t("offers.workspace.reset")}
          </button>
        </div>
      </section>
    </div>
  );
}
